import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { TagsList } from '@creditflowresearch/uilib/Input/InputTags/TagsList';
import { TagsInput } from '@creditflowresearch/uilib/Input/InputTags/TagsInput';

import { inputStyles } from '@creditflowresearch/uilib/Input';
import styles from './inputTags.module.scss';

export interface IValidationError {
  [key: string]: string,
}

export interface IInputTag {
  isValid: boolean,
  value: string,
  label?: string,
  customIcon?: React.FC,
}

export interface IProps {
  /**
   * The list of input tags
   */
  tags: IInputTag[],
  /**
   * Optional CSS class name for styling the wrapper
   */
  wrapperClassName?: string,
  /**
   * Optional CSS class name for styling the container
   */
  containerClassName?: string,
  /**
   * Optional CSS class name for styling the error message wrapper
   */
  errorMessageWrapperClassName?: string,
  /**
   * A component to render before the input field
   */
  prependComponent?: React.ReactNode,
  /**
   * A component to render after the input field
   */
  appendComponent?: React.ReactNode,
  /**
   * Whether the input field is disabled
   */
  isDisabled?: boolean,
  /**
   * Whether the component is in a loading state
   */
  isFetching?: boolean,
  /**
   * Whether the input should be displayed in a narrow style
   */
  isNarrow?: boolean,
  /**
   * Placeholder text for the input field
   */
  placeholder?: string,
  /**
   * Error message to display when validation fails
   */
  errorMessage?: string | React.ReactNode,
  /**
   * Whether to display validation errors
   */
  withErrors?: boolean,
  /**
   * Validation function for tag input
   */
  validate?: (value: string) => IValidationError | null,
  /**
   * Normalization function to transform the input value
   */
  normalize?: (value: string) => string,
  /**
   * Function to normalize tag values when they change
   */
  normalizeChange?: (value: string) => string[],
  /**
   * Callback function triggered when the tags list changes
   */
  onChange?: (tags: IInputTag[], tagsList?: IInputTag[]) => void,
  /**
   * Callback function triggered when tags are submitted
   */
  onSubmitTags?: () => void,
  /**
   * Unique identifier for testing purposes
   */
  dataTest: string,
  /**
   * Whether the input field should autofocus on mount
   */
  autoFocus?: boolean,
  /**
   * Callback function triggered when the input gains focus
   */
  onFocusHandler?: () => void,
  /**
   * Callback function triggered when the input loses focus
   */
  onBlurHandler?: () => void,
  /**
   * Keys that will trigger tag submission when pressed
   */
  submitEnteredTagKeys?: string[],

  /**
   * Whether the input field is disabled for user input
   */
  isInputDisabled?: boolean,

  /**
   * A component to render before the tags inside the input field
   */
  inputPrependComponent?: React.ReactNode,
}

const InputTags = ({
  tags,
  wrapperClassName,
  containerClassName,
  errorMessageWrapperClassName,
  prependComponent,
  appendComponent,
  inputPrependComponent,
  isDisabled,
  isFetching,
  isNarrow,
  autoFocus = true,
  placeholder,
  errorMessage,
  withErrors,
  validate,
  normalize,
  normalizeChange,
  onChange = noop,
  onFocusHandler = noop,
  onBlurHandler = noop,
  onSubmitTags = noop,
  dataTest = 'InputTags',
  submitEnteredTagKeys,
  isInputDisabled,
}: IProps) => {
  const inputRef = useRef(null) as React.MutableRefObject<HTMLInputElement>;
  const containerRef = useRef(null);
  const [hasErrors, setHasErrors] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const inputPlaceholder = useMemo(() => (
    tags.length ? '' : placeholder
  ), [tags.length, placeholder]);

  useEffect(() => {
    setHasErrors(withErrors || tags.some(({ isValid }) => !isValid));
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
    isActive && inputRef.current.focus();
  }, [tags, withErrors]);

  const appendTags = (tagsList) => {
    onChange(tags.concat(tagsList), tagsList);
  };

  const removeTagByIndex = useCallback((tagIndex: number) => {
    onChange(tags.filter((_, index) => index !== tagIndex));
    inputRef.current.focus();
  }, [tags]);

  const removeLastTag = () => {
    removeTagByIndex(tags.length - 1);
  };

  const submitTags = () => {
    !isFetching && !hasErrors && onSubmitTags();
  };

  const handleInputFocus = () => {
    setIsActive(true);
    onFocusHandler();
  };

  const handleInputBlur = () => {
    if (!isFetching) {
      setIsActive(false);
      onBlurHandler();
    }
  };

  return (
    <>
      <div
        data-test={ `${ dataTest }Wrapper` }
        className={ cn(styles.inputTagsWrp, wrapperClassName, {
          [styles.isPrepended]: !!prependComponent,
          [styles.isAppended]: !!appendComponent,
          [styles.isDisabled]: isDisabled,
          [styles.isInvalid]: hasErrors,
          [styles.isActive]: isActive,
          [styles.isNarrow]: isNarrow,
        }) }
      >
        { prependComponent }
        <div
          data-test={ `${ dataTest }Container` }
          className={ cn(
            styles.inputTagsContainer,
            inputStyles.formInput,
            containerClassName,
          ) }
          ref={ containerRef }
        >
          { inputPrependComponent }
          <TagsList
            dataTest={ dataTest }
            tags={ tags }
            isDisabled={ isDisabled }
            onRemoveTag={ removeTagByIndex }
          />
          { /* @ts-ignore */ }
          <TagsInput
            ref={ inputRef }
            tags={ tags }
            isDisabled={ isDisabled }
            isInputDisabled={ isInputDisabled }
            isFetching={ isFetching }
            placeholder={ inputPlaceholder }
            onAppendTags={ appendTags }
            onRemoveTag={ removeLastTag }
            onSubmitTags={ submitTags }
            onFocus={ handleInputFocus }
            onBlur={ handleInputBlur }
            validate={ validate }
            normalize={ normalize }
            normalizeChange={ normalizeChange }
            dataTest={ dataTest }
            autoFocus={ autoFocus }
            submitEnteredTagKeys={ submitEnteredTagKeys }
          />
        </div>
        { appendComponent }
      </div>
      { hasErrors && (
        <div
          data-test={ `${ dataTest }ErrorMessage` }
          className={ cn(styles.errorMessageWrapper, errorMessageWrapperClassName) }
        >
          { errorMessage }
        </div>
      ) }
    </>
  );
};

export default InputTags;
